import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { API } from '../lib/api';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import { Button } from '../components/ui/button';
import { SparkleField } from '../components/SparkleField';
import {
  ShoppingBag, Sparkles, Flame, AlertTriangle, ArrowLeft, Check,
  Star, Ruler, Weight, Package as PackageIcon, MessageCircle,
} from 'lucide-react';
import { motion } from 'framer-motion';

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const { settings } = useSettings();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setActiveImg(0);
    Promise.all([
      api.get(`/products/${id}`).then((r) => setProduct(r.data)),
      api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => setReviews([])),
    ])
      .catch(() => setError('Producto no encontrado'))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500">
        <Sparkles className="h-6 w-6 mr-2 animate-pulse text-pinky-400" /> Cargando…
      </div>
    );
  }
  if (error || !product) {
    return (
      <div className="max-w-lg mx-auto text-center py-20 px-4">
        <h1 className="font-display text-3xl text-slate-900">Producto no encontrado</h1>
        <p className="text-slate-500 mt-2">Es posible que haya sido eliminado del catálogo.</p>
        <Link to="/" className="inline-block mt-6 px-6 py-3 rounded-full btn-pink font-bold">← Volver al catálogo</Link>
      </div>
    );
  }

  const gallery = [product.image, ...(product.images || [])].filter(Boolean);
  // Para miniaturas del carrusel usamos la versión thumb si es endpoint propio
  const thumbUrl = (url) => {
    if (!url) return url;
    if (url.startsWith('/api/products/') && !url.includes('?')) return url + '?s=thumb';
    return url;
  };
  const threshold = Math.max(1, parseInt(settings?.low_stock_threshold, 10) || 5);
  const outOfStock = product.stock === 0;
  const lowStock = product.stock > 0 && product.stock <= threshold;
  const price = product.is_offer && product.offer_price ? product.offer_price : product.price;
  const avgRating = reviews.length ? reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length : 0;
  const waNumber = (settings.whatsapp_number || '').replace(/\D/g, '');
  const consultUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(`Hola, tengo una consulta sobre el producto: ${product.name}`)}`;

  return (
    <main data-testid="product-detail-page" className="relative">
      <SparkleField mobileCount={1} desktopCount={3} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-pinky-600 mb-6" data-testid="back-to-catalog">
          <ArrowLeft className="h-4 w-4" /> Volver al catálogo
        </Link>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* GALLERY */}
          <div>
            <div className="relative aspect-square rounded-3xl overflow-hidden bg-pinky-50 soft-card p-0 pink-glow">
              {gallery[activeImg] ? (
                <img
                  src={gallery[activeImg]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                  fetchPriority="high"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-pinky-300">
                  <Sparkles className="h-24 w-24" strokeWidth={1.2} />
                </div>
              )}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {product.is_new && (
                  <span className="px-3 py-1.5 rounded-full bg-pink-100 text-pink-700 text-xs font-bold border border-pink-200 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> Nuevo
                  </span>
                )}
                {product.is_offer && (
                  <span className="px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-bold border border-purple-200 flex items-center gap-1">
                    <Flame className="h-3 w-3" /> Oferta
                  </span>
                )}
                {lowStock && (
                  <span className="px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold border border-amber-200 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Pocas unidades
                  </span>
                )}
              </div>
            </div>

            {gallery.length > 1 && (
              <div className="mt-4 flex gap-2 flex-wrap" data-testid="product-thumbnails">
                {gallery.map((img, i) => (
                  <button
                    key={`thumb-${i}-${img.slice(-32)}`}
                    onClick={() => setActiveImg(i)}
                    className={`h-20 w-20 rounded-2xl overflow-hidden border-2 transition-all ${activeImg === i ? 'border-pinky-500 pink-glow' : 'border-pinky-100 hover:border-pinky-300'}`}
                    data-testid={`product-thumb-${i}`}
                  >
                    <img src={thumbUrl(img)} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* INFO */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {product.product_type && (
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-pinky-500 mb-2">{product.product_type}</p>
            )}
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight" data-testid="product-name">{product.name}</h1>

            {reviews.length > 0 && (
              <div className="mt-3 flex items-center gap-2">
                <div className="flex text-amber-400">
                  {[...Array(5)].map((_, i) => (
                    <Star key={`prod-star-${i}`} className={`h-4 w-4 ${i < Math.round(avgRating) ? 'fill-amber-400' : 'text-slate-300'}`} />
                  ))}
                </div>
                <span className="text-sm text-slate-600 font-semibold">{avgRating.toFixed(1)}</span>
                <span className="text-sm text-slate-400">({reviews.length} {reviews.length === 1 ? 'reseña' : 'reseñas'})</span>
              </div>
            )}

            <div className="mt-5 flex items-baseline gap-3">
              <span className="font-display text-5xl text-pinky-600 font-bold" data-testid="product-detail-price">Q{price.toFixed(0)}</span>
              {product.is_offer && product.offer_price && (
                <span className="text-xl text-slate-400 line-through">Q{product.price.toFixed(0)}</span>
              )}
            </div>

            {product.description && (
              <p className="mt-5 text-slate-600 leading-relaxed">{product.description}</p>
            )}

            {/* Quick specs */}
            {(product.size || product.weight || product.product_type) && (
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {product.size && (
                  <div className="p-3 rounded-2xl bg-pinky-50 border border-pinky-100">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1"><Ruler className="h-3 w-3" /> Tamaño</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{product.size}</p>
                  </div>
                )}
                {product.weight && (
                  <div className="p-3 rounded-2xl bg-pinky-50 border border-pinky-100">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1"><Weight className="h-3 w-3" /> Peso</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{product.weight}</p>
                  </div>
                )}
                {product.product_type && (
                  <div className="p-3 rounded-2xl bg-pinky-50 border border-pinky-100">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1"><PackageIcon className="h-3 w-3" /> Tipo</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{product.product_type}</p>
                  </div>
                )}
              </div>
            )}

            {/* Stock indicator */}
            <div className="mt-6 flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${outOfStock ? 'bg-rose-500' : lowStock ? 'bg-amber-500' : 'bg-emerald-500'}`} />
              <span className={`text-sm font-semibold ${outOfStock ? 'text-rose-600' : lowStock ? 'text-amber-700' : 'text-emerald-700'}`}>
                {outOfStock ? 'Agotado' : lowStock ? `¡Solo ${product.stock} unidades disponibles!` : `${product.stock} disponibles — Entrega inmediata 🇬🇹`}
              </span>
            </div>

            {/* CTAs */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="detail-add-to-cart"
                onClick={() => addItem(product)}
                disabled={outOfStock}
                size="lg"
                className="btn-pink rounded-full h-14 px-8 text-base font-bold flex-1 gap-2"
              >
                <ShoppingBag className="h-5 w-5" /> Agregar al carrito
              </Button>
              <Button asChild size="lg" className="btn-pink-outline rounded-full h-14 px-6 font-bold" data-testid="detail-consult-btn">
                <a href={consultUrl} target="_blank" rel="noreferrer">
                  <MessageCircle className="h-5 w-5 mr-1" /> Consultar
                </a>
              </Button>
            </div>

            {/* Features */}
            {product.features?.length > 0 && (
              <div className="mt-8 soft-card p-5" data-testid="product-features">
                <h3 className="font-display text-lg text-slate-900 mb-3 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-pinky-500" /> Características
                </h3>
                <ul className="space-y-2">
                  {product.features.map((f) => (
                    <li key={f} className="flex gap-2 text-sm text-slate-700">
                      <Check className="h-4 w-4 text-pinky-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        </div>

        {/* REVIEWS */}
        <section className="mt-16">
          <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-pinky-500">Reseñas</span>
              <h2 className="font-display text-3xl text-slate-900 mt-1">Lo que dicen las compradoras</h2>
            </div>
            {reviews.length > 0 && (
              <div className="flex items-center gap-2">
                <div className="flex text-amber-400">
                  {[...Array(5)].map((_, i) => (
                    <Star key={`avg-star-${i}`} className={`h-5 w-5 ${i < Math.round(avgRating) ? 'fill-amber-400' : 'text-slate-300'}`} />
                  ))}
                </div>
                <span className="font-bold text-slate-800">{avgRating.toFixed(1)}/5</span>
              </div>
            )}
          </div>

          {reviews.length === 0 ? (
            <div className="soft-card p-8 text-center text-slate-500" data-testid="no-reviews">
              <Sparkles className="h-10 w-10 mx-auto text-pinky-300 mb-2" />
              Todavía no hay reseñas de este producto.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="product-reviews">
              {reviews.map((r) => (
                <div key={r.id} className="soft-card p-5" data-testid={`review-${r.id}`}>
                  <div className="flex items-center gap-1 text-amber-400 mb-2">
                    {[...Array(r.rating || 5)].map((_, i) => <Star key={`review-${r.id}-${i}`} className="h-4 w-4 fill-amber-400" />)}
                  </div>
                  <p className="text-slate-700 italic">"{r.comment}"</p>
                  <p className="mt-3 pt-3 border-t border-pinky-100 text-sm font-bold text-slate-800">{r.name}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
